import * as React from "react";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import MailIcon from "@mui/icons-material/Mail";
import Popover from "@mui/material/Popover";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  Modal,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useAppStore } from "../../appStore";
import moment from "moment";
import { api_url, img_url } from "../../apiUtils";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import SafetyCheckIcon from "@mui/icons-material/SafetyCheck";
import VerificationSuccessDialog from "./verificationDialog";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#ff6d00",
    color: "#ffffff",
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
      color: "#ff6d00",
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(1.6)",
      opacity: 0,
    },
  },
}));

const MessageCard = styled(CardContent)(({ isNew }) => ({
  padding: "3px 2px 2px 10px",
  margin: "0.5rem",
  background: "#f5f5f5",
  borderRadius: 3,
  cursor: "pointer",
  borderLeft: isNew && "4px solid #673ab7",
}));

export default function Notification() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { profileData, message, setMessage } = useAppStore();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [recipientMobile, setRecipientMobile] = useState("");
  const [secretCode, setSecretCode] = useState("");
  const [errors, setErrors] = useState({});
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const handleOpenPreview = () => setOpenPreview(true);
  const handleClosePreview = () => setOpenPreview(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenModal = async (msg) => {
    try {
      await axios.post(
        `${api_url}user/inbox/markAsRead`,
        { messageId: msg._id },
        {
          headers: {
            accesstoken: sessionStorage.getItem("accesstoken"),
          },
        }
      );

      // Update message list locally
      const updated = message?.map((m) =>
        m._id === msg._id ? { ...m, read: true } : m
      );
      setMessage(updated);
    } catch (error) {
      console.error("Error marking message as read", error);
    }

    setSelectedMessage(msg);
    setOpenModal(true);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const unreadCount = message?.filter((m) => !m.read).length;

  // delete
  const handleDeleteMessage = async () => {
    try {
      await axios.post(
        `${api_url}user/inbox/message/delete`,
        { messageId: messageToDelete._id },
        {
          headers: {
            accesstoken: sessionStorage.getItem("accesstoken"),
          },
        }
      );

      // Remove from local state
      const updated = message?.filter((msg) => msg._id !== messageToDelete._id);
      setMessage(updated);
      toast.success("Message deleted successfully");
    } catch (error) {
      console.error("Error deleting message", error);
      toast.error("Failed to delete message");
    } finally {
      setDeleteConfirmOpen(false);
      setMessageToDelete(null);
    }
  };

  const handleOpenVerifyModal = () => {
    setVerifyModalOpen(true);
    setOpenModal(false);
    setAnchorEl(null);
  };

  const handleCloseVerifyModal = () => {
    setVerifyModalOpen(false);
    setRecipientMobile("");
    setSecretCode("");
    setErrors({});
  };

  const handleVerifySubmit = async () => {
    const newErrors = {};
    if (!recipientMobile.trim()) newErrors.recipientMobile = "Required";
    if (!secretCode.trim()) newErrors.secretCode = "Required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const response = await axios.post(
        `${api_url}wallet/offline-transfer/verify`, // Replace with actual endpoint
        {
          senderMobile: recipientMobile,
          secretCode: secretCode,
        },
        {
          headers: {
            accesstoken: sessionStorage.getItem("accesstoken"),
          },
        }
      );

      if (response?.data?.success) {
        setSuccessDialogOpen(true);
        handleCloseVerifyModal();
      }
    } catch (error) {
      toast.error("Verification failed");
      console.error("Verification error", error);
    }
  };

  return (
    <Stack
      spacing={4}
      direction="row"
      sx={{ color: "action.active", cursor: "pointer" }}
    >
      <StyledBadge
        color="error"
        badgeContent={unreadCount}
        showZero
        onClick={handleClick}
      >
        <MailIcon sx={{ color: { xs: "#e0e0e0", md: "#757575" } }} />
      </StyledBadge>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{ mt: 3 }}
      >
        <Card sx={{ width: "240px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", color: "#1e2466" }}
            >
              Messages
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "#ff5722" }}
            >
              Unread ({unreadCount})
            </Typography>
          </Box>

          <Box
            sx={{
              mr: 0.5,
              maxHeight: "250px",
              overflowY: "auto",
              "&:hover": {
                overflowY: "auto",
              },
              "&::-webkit-scrollbar": {
                width: "6px",
                display: "none",
              },
              "&:hover::-webkit-scrollbar": {
                display: "block",
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: `#e9e9e9`,
                borderRadius: "4px",
              },
            }}
          >
            {message?.length > 0 ? (
              message?.map((msg) => (
                <MessageCard
                  key={msg._id}
                  isNew={!msg.read}
                  onClick={() => handleOpenModal(msg)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMessageToDelete(msg); // Set message for deletion
                      setDeleteConfirmOpen(true); // Open modal
                    }}
                    sx={{ position: "absolute", top: 1, right: 4 }}
                  >
                    <DeleteIcon
                      fontSize="small"
                      sx={{
                        color: "gray",
                        fontSize: "18px",
                        "&:hover": {
                          color: "red",
                        },
                      }}
                    />
                  </IconButton>

                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: "12px",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      width: "100%", // Or a fixed width like "150px" if you want strict truncation
                    }}
                    title={msg.heading} // Shows full heading on hover
                  >
                    {msg.heading.length > 20
                      ? `${msg.heading.slice(0, 20)}...`
                      : msg.heading}
                  </Typography>

                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {msg.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "10px", color: "#80cbc4" }}
                  >
                    {moment(msg.createdAt).fromNow()}
                  </Typography>
                </MessageCard>
              ))
            ) : (
              <CardContent sx={{ textAlign: "center" }}>
                <Typography>No messages</Typography>
              </CardContent>
            )}
          </Box>
        </Card>
      </Popover>

      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 320,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Delete Message
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Are you sure you want to delete this message?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              onClick={() => setDeleteConfirmOpen(false)}
              sx={{ bgcolor: "#c8e6c9", ":hover": { bgcolor: "#a5d6a7" } }}
            >
              <Typography variant="button">Cancel</Typography>
            </Button>
            <Button
              color="error"
              onClick={handleDeleteMessage}
              sx={{ bgcolor: "#ffcdd2", ":hover": { bgcolor: "#ef9a9a" } }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        closeAfterTransition
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: 300, sm: 400 },
              outline: "none",
            }}
          >
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 6,
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  borderBottom: "1px solid #eee",
                  backgroundColor: "#f9f9f9",
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {selectedMessage?.heading}
                </Typography>
                <IconButton
                  onClick={() => {
                    setOpenModal(false);
                    setAnchorEl(null);
                  }}
                  sx={{
                    "&:hover": { color: "red" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <CardContent sx={{ p: 2 }}>
                {selectedMessage ? (
                  <>
                    {/* <Typography
                      variant="subtitle1"
                      gutterBottom
                      fontWeight="600"
                      sx={{ mb: 2 }} // Add some bottom margin for spacing
                    >
                      {selectedMessage?.heading}
                    </Typography> */}

                    <Typography
                      variant="body1"
                      sx={{ color: "text.secondary", mb: 2 }}
                    >
                      {selectedMessage?.message}
                    </Typography>

                    {/* If image exists, show the "View Image" button */}
                    {(selectedMessage?.image ||
                      selectedMessage?.heading === "Offline Transaction") && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        {selectedMessage?.image && (
                          <Button
                            variant="outlined"
                            onClick={handleOpenPreview}
                            size="small"
                            sx={{
                              color: "#1e2466",
                              borderColor: "#1e2466",
                            }}
                          >
                            View Image
                          </Button>
                        )}

                        {selectedMessage?.heading === "Offline Transaction" && (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              color: "green",
                              borderColor: "green",
                              letterSpacing: 1,
                            }}
                            onClick={handleOpenVerifyModal}
                          >
                            Click to Verify
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* If no image, just show the date */}
                    {!selectedMessage?.image && (
                      <Typography
                        variant="caption"
                        sx={{ color: "gray", fontStyle: "italic" }}
                      >
                        {moment(selectedMessage?.createdAt).format(
                          "MMMM Do YYYY, h:mm:ss a"
                        )}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No message selected.
                  </Typography>
                )}

                {/* Image Preview Dialog */}
                <Dialog open={openPreview} onClose={handleClosePreview}>
                  <DialogTitle>Image Preview</DialogTitle>
                  <DialogContent>
                    <img
                      src={`${img_url}${selectedMessage?.image}`}
                      alt="Message Attachment"
                      style={{
                        width: "100%",
                        maxHeight: "400px",
                        objectFit: "contain",
                      }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleClosePreview} color="primary">
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Modal>

      <Modal open={verifyModalOpen}>
        <Fade in={verifyModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 320,
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              p: 3,
            }}
          >
            <Typography
              variant="h6"
              mb={2}
              sx={{
                color: "#212762",
                fontWeight: "bold",
                letterSpacing: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              <SafetyCheckIcon sx={{ mr: 1 }} /> Verify Offline Transaction
            </Typography>

            <Box mb={3}>
              <Typography variant="body2" mb={0.5} sx={{ color: "#212121" }}>
                Enter ID Mobile Number
              </Typography>
              <input
                type="text"
                value={recipientMobile}
                onChange={(e) => setRecipientMobile(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: `1px solid ${
                    errors.recipientMobile ? "red" : "#ccc"
                  }`,
                  borderRadius: "4px",
                }}
              />
              {errors.recipientMobile && (
                <Typography variant="caption" color="error">
                  {errors.recipientMobile}
                </Typography>
              )}
            </Box>

            <Box mb={3} sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" mb={0.5} sx={{ color: "#212121" }}>
                Secret Code :
              </Typography>
              <input
                type="text"
                value={secretCode}
                onChange={(e) => setSecretCode(e.target.value)}
                style={{
                  width: "auto",
                  padding: "8px",
                  border: `1px solid ${errors.secretCode ? "red" : "#ccc"}`,
                  borderRadius: "4px",
                  marginLeft: 2,
                }}
              />
              {errors.secretCode && (
                <Typography variant="caption" color="error">
                  {errors.secretCode}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 6,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleCloseVerifyModal}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ background: "green" }}
                onClick={handleVerifySubmit}
              >
                Verify & Submit
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
      <VerificationSuccessDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
      />
    </Stack>
  );
}
